import React, { useEffect, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Button,
  TableContainer,
  InputAdornment
} from '@mui/material';

import { getVitals, saveVitals } from '../api/vitalsApi';

export default function ParametriVitaliTable() {
  const [columns, setColumns] = useState([]);
  const [original, setOriginal] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadVitals();
  }, []);

  const loadVitals = async () => {
    try {
      const data = await getVitals();

      setColumns(data);
      setOriginal(structuredClone(data));
    } catch (err) {
      console.error('Errore caricamento parametri:', err);
    }
  };

  const rows = [
    {
      label: 'Data e ora',
      field: 'datetime',
      placeholder: '',
      unit: ''
    },
    {
      label: 'Pressione Arteriosa (PA)',
      field: 'pa',
      placeholder: '120/80',
      unit: ''
    },
    {
      label: 'Frequenza Cardiaca (FC)',
      field: 'fc',
      placeholder: '',
      unit: 'bpm'
    },
    {
      label: 'Frequenza Respiratoria (FR)',
      field: 'fr',
      placeholder: '',
      unit: 'atti/min'
    },
    {
      label: 'Saturazione Ossigeno (SaO₂)',
      field: 'sat',
      placeholder: '',
      unit: '%'
    },
    {
      label: 'Temperatura Corporea (TC)',
      field: 'tc',
      placeholder: '',
      unit: '°C'
    },
    {
      label: 'Dolore (NRS)',
      field: 'pain',
      placeholder: '',
      unit: '/10'
    },
    {
      label: 'Peso',
      field: 'weight',
      placeholder: '',
      unit: 'Kg'
    },
    {
      label: 'Altezza',
      field: 'height',
      placeholder: '',
      unit: 'cm'
    }
  ];

  const handleChange = (colIndex, field, value) => {
    setColumns(prev =>
      prev.map((col, i) =>
        i === colIndex
          ? {
              ...col,
              [field]: value
            }
          : col
      )
    );
  };

  const addRilevazione = () => {
    const now = new Date();

    const formatted =
      now.toLocaleDateString() +
      ' ' +
      now.toLocaleTimeString().slice(0, 5);

    const newRecord = {
      id: crypto.randomUUID(),
      datetime: formatted,
      pa: '',
      fc: '',
      fr: '',
      sat: '',
      tc: '',
      pain: '',
      weight: '',
      height: ''
    };

    setColumns(prev => [newRecord, ...prev]);
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      await saveVitals(columns);

      setOriginal(structuredClone(columns));
    } catch (err) {
      console.error('Errore salvataggio:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = () => {
    setColumns(structuredClone(original));
  };

  return (
    <Box sx={{ width: '50ch', m: 1 }}>
      {/* HEADER */}
      <Box
        sx={{
          p: 3,
          mb: 2,
          border: '1px solid rgba(0,0,0,0.23)',
          borderRadius: 2,
          backgroundColor: '#f5f5f5',
          textAlign: 'center'
        }}
      >
        <Box
          component="h1"
          sx={{
            m: 0,
            fontSize: '1.6rem',
            color: '#1976d2'
          }}
        >
          Parametri Vitali ed Auxologici
        </Box>

        <Box
          component="p"
          sx={{
            m: 0,
            mt: 1,
            color: 'text.secondary'
          }}
        >
          Inserimento e monitoraggio delle rilevazioni
        </Box>
      </Box>

      {/* TABELLA + BOTTONE LATERALE */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          gap: 1
        }}
      >
        <TableContainer
          sx={{
            overflowX: 'auto',
            border: '1px solid rgba(0,0,0,0.23)',
            maxWidth: '100%'
          }}
        >
          <Table
            sx={{
              tableLayout: 'fixed',
              minWidth: columns.length * 220 + 260,

              '& td': {
                border: '1px solid rgba(0,0,0,0.23)',
                minWidth: 220,
                p: 1,
                verticalAlign: 'top'
              },

              '& td:first-of-type': {
                backgroundColor: '#f5f5f5',
                fontWeight: 'bold',
                minWidth: 260,
                whiteSpace: 'nowrap'
              }
            }}
          >
            <TableBody>
              {rows.map(row => (
                <TableRow key={row.field}>
                  <TableCell>{row.label}</TableCell>

                  {columns.map((col, colIndex) => (
                    <TableCell key={col.id}>
                      <TextField
                        size="small"
                        value={col[row.field] ?? ''}
                        placeholder={row.placeholder}
                        onChange={(e) =>
                          handleChange(
                            colIndex,
                            row.field,
                            e.target.value
                          )
                        }
                        sx={{ width: '100%' }}
                        InputProps={{
                          endAdornment: row.unit ? (
                            <InputAdornment position="end">
                              {row.unit}
                            </InputAdornment>
                          ) : null
                        }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* BOTTONE VERTICALE */}
        <Button
          onClick={addRilevazione}
          variant="contained"
          color="primary"
          sx={{
            writingMode: 'vertical-rl',
            transform: 'rotate(180deg)',
            minWidth: 42,
            flexShrink: 0,
            borderRadius: 1
          }}
        >
          AGGIUNGI RILEVAZIONE
        </Button>
      </Box>

      {/* BOTTONI IN BASSO A DESTRA */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          mt: 2
        }}
      >
        <Button
          variant="outlined"
          color="warning"
          onClick={handleRestore}
        >
          Ripristina
        </Button>

        <Button
          variant="contained"
          color="success"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Salvataggio...' : 'Salva'}
        </Button>
      </Box>
    </Box>
  );
}